import { FC } from 'react';
import { Clock, CheckCircle, Ban, XCircle, MinusCircle, Users } from 'lucide-react';
import { cn } from '../../../utils/cn';
import type { TherapistStatusCounts as StatusCounts } from '../../../api/adminTherapistApi';

interface TherapistStatusCountsProps {
  counts: StatusCounts | undefined;
  isLoading?: boolean;
  activeFilter?: string;
  onFilterClick?: (status: string) => void;
  className?: string;
}

interface StatusCardConfig {
  key: keyof Omit<StatusCounts, 'total'> | 'total';
  label: string;
  icon: typeof Users;
  bgColor: string;
  iconBgColor: string;
  iconColor: string;
}

const statusCards: StatusCardConfig[] = [
  {
    key: 'total',
    label: 'Total',
    icon: Users,
    bgColor: 'hover:border-gray-300 dark:hover:border-slate-600',
    iconBgColor: 'bg-gray-100 dark:bg-slate-700',
    iconColor: 'text-gray-600 dark:text-slate-400',
  },
  {
    key: 'PENDING',
    label: 'Pending',
    icon: Clock,
    bgColor: 'hover:border-yellow-300 dark:hover:border-yellow-700',
    iconBgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  {
    key: 'ACTIVE',
    label: 'Active',
    icon: CheckCircle,
    bgColor: 'hover:border-green-300 dark:hover:border-green-700',
    iconBgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  {
    key: 'SUSPENDED',
    label: 'Suspended',
    icon: Ban,
    bgColor: 'hover:border-orange-300 dark:hover:border-orange-700',
    iconBgColor: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'text-orange-600 dark:text-orange-400',
  },
  {
    key: 'REJECTED',
    label: 'Rejected',
    icon: XCircle,
    bgColor: 'hover:border-red-300 dark:hover:border-red-700',
    iconBgColor: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  {
    key: 'INACTIVE',
    label: 'Inactive',
    icon: MinusCircle,
    bgColor: 'hover:border-gray-400 dark:hover:border-slate-500',
    iconBgColor: 'bg-gray-200 dark:bg-slate-600',
    iconColor: 'text-gray-500 dark:text-slate-400',
  },
];

export const TherapistStatusCounts: FC<TherapistStatusCountsProps> = ({
  counts,
  isLoading = false,
  activeFilter,
  onFilterClick,
  className,
}) => {
  if (isLoading) {
    return (
      <div className={cn('grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4', className)}>
        {statusCards.map((card) => (
          <div
            key={card.key}
            className="h-24 animate-pulse rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4', className)}>
      {statusCards.map((card) => {
        const Icon = card.icon;
        const count = counts?.[card.key] ?? 0;
        const isActive = activeFilter === card.key || (activeFilter === '' && card.key === 'total');

        return (
          <button
            key={card.key}
            onClick={() => onFilterClick?.(card.key === 'total' ? '' : card.key)}
            className={cn(
              'relative rounded-xl border-2 p-4 text-left transition-all',
              'bg-white dark:bg-slate-800',
              isActive
                ? 'border-blue-500 dark:border-blue-400 shadow-md'
                : 'border-gray-100 dark:border-slate-700',
              card.bgColor,
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900'
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                  {card.label}
                </p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-slate-100">{count}</p>
              </div>
              <div className={cn('rounded-lg p-2', card.iconBgColor)}>
                <Icon className={cn('h-5 w-5', card.iconColor)} />
              </div>
            </div>
            {isActive && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-blue-500 dark:bg-blue-400 rounded-full" />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default TherapistStatusCounts;
